import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Stethoscope,
  Heart,
  Droplets,
  Phone,
  X,
  Edit3,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { usePregnancyInfo } from '../hooks/usePregnancyInfo';

interface PregnancyInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PregnancyInfoModal({ isOpen, onClose }: PregnancyInfoModalProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = usePregnancyInfo();

  if (!isOpen) return null;

  const handleEdit = () => {
    onClose();
    navigate('/profile/pregnancy-info');
  };

  // Click-to-Call Handler
  const handleCall = (phone: string | undefined) => {
    if (!phone) return;
    // Telefonnummer bereinigen
    const cleanPhone = phone.replace(/\s/g, '');
    window.location.href = `tel:${cleanPhone}`;
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>Schwangerschafts-Infokarte</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-400">
              <p>{error}</p>
            </div>
          ) : !data ? (
            <div className="text-center py-8 text-white/70">
              <p className="mb-4">Noch keine Daten hinterlegt</p>
              <Button onClick={handleEdit} className="bg-pp-orange hover:bg-pp-orange/90">
                <Edit3 className="w-4 h-4 mr-2" />
                Jetzt ausfüllen
              </Button>
            </div>
          ) : (
            <>
              {/* Krankenhaus */}
              {(data.hospital?.name || data.hospital?.phone) && (
                <InfoSection
                  icon={<Building2 className="w-5 h-5" />}
                  title="Krankenhaus"
                >
                  {data.hospital?.name && (
                    <p className="font-medium text-white">{data.hospital.name}</p>
                  )}
                  {data.hospital?.address && (
                    <p className="text-sm text-white/70">{data.hospital.address}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.hospital?.phone && (
                      <PhoneButton
                        label="Telefon"
                        phone={data.hospital.phone}
                        onClick={() => handleCall(data.hospital?.phone)}
                      />
                    )}
                    {data.hospital?.delivery_phone && (
                      <PhoneButton
                        label="Kreißsaal"
                        phone={data.hospital.delivery_phone}
                        onClick={() => handleCall(data.hospital?.delivery_phone)}
                        highlight
                      />
                    )}
                  </div>
                </InfoSection>
              )}

              {/* Frauenarzt */}
              {(data.gynecologist?.practice || data.gynecologist?.phone) && (
                <InfoSection
                  icon={<Stethoscope className="w-5 h-5" />}
                  title="Frauenarzt/ärztin"
                >
                  {data.gynecologist?.practice && (
                    <p className="font-medium text-white">{data.gynecologist.practice}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.gynecologist?.phone && (
                      <PhoneButton
                        label="Praxis"
                        phone={data.gynecologist.phone}
                        onClick={() => handleCall(data.gynecologist?.phone)}
                      />
                    )}
                    {data.gynecologist?.emergency_phone && (
                      <PhoneButton
                        label="Notfall"
                        phone={data.gynecologist.emergency_phone}
                        onClick={() => handleCall(data.gynecologist?.emergency_phone)}
                        highlight
                      />
                    )}
                  </div>
                </InfoSection>
              )}

              {/* Hebamme */}
              {(data.midwife?.name || data.midwife?.phone) && (
                <InfoSection
                  icon={<Heart className="w-5 h-5" />}
                  title="Hebamme"
                >
                  {data.midwife?.name && (
                    <p className="font-medium text-white">{data.midwife.name}</p>
                  )}
                  {data.midwife?.on_call_from_ssw && (
                    <p className="text-sm text-white/70">
                      Rufbereitschaft ab SSW {data.midwife.on_call_from_ssw}
                    </p>
                  )}
                  {data.midwife?.phone && (
                    <div className="mt-2">
                      <PhoneButton
                        label="Telefon"
                        phone={data.midwife.phone}
                        onClick={() => handleCall(data.midwife?.phone)}
                      />
                    </div>
                  )}
                </InfoSection>
              )}

              {/* Medizinische Daten */}
              {(data.medical_data?.blood_type || data.medical_data?.allergies) && (
                <InfoSection
                  icon={<Droplets className="w-5 h-5" />}
                  title="Medizinische Daten"
                >
                  {data.medical_data?.blood_type && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white/70">Blutgruppe:</span>
                      <span className="font-semibold text-pp-orange">
                        {data.medical_data.blood_type}
                        {data.medical_data?.rhesus_factor && (
                          <span className="ml-1">
                            ({data.medical_data.rhesus_factor === 'positiv' ? 'Rh+' : 'Rh-'})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {data.medical_data?.allergies && (
                    <div className="mt-1">
                      <span className="text-sm text-white/70">Allergien:</span>
                      <p className="text-pp-orange font-medium">{data.medical_data.allergies}</p>
                    </div>
                  )}
                  {data.medical_data?.notes && (
                    <div className="mt-1">
                      <span className="text-sm text-white/70">Besonderheiten:</span>
                      <p className="text-white">{data.medical_data.notes}</p>
                    </div>
                  )}
                </InfoSection>
              )}

              {/* Notfallkontakte */}
              {data.emergency_contacts && data.emergency_contacts.length > 0 && (
                <InfoSection
                  icon={<Phone className="w-5 h-5" />}
                  title="Notfallkontakte"
                >
                  <div className="space-y-2">
                    {data.emergency_contacts.map((contact, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{contact.name}</p>
                          <p className="text-sm text-white/70">{contact.relationship}</p>
                        </div>
                        <PhoneButton
                          phone={contact.phone}
                          onClick={() => handleCall(contact.phone)}
                          compact
                        />
                      </div>
                    ))}
                  </div>
                </InfoSection>
              )}
            </>
          )}
          </div>

          {/* Footer */}
          {data && (
            <div className="bottom-sheet-footer">
              <Button onClick={handleEdit} className="flex-1 bg-pp-orange hover:bg-pp-orange/90">
                <Edit3 className="w-4 h-4 mr-2" />
                Bearbeiten
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

// Hilfskomponenten

interface InfoSectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function InfoSection({ icon, title, children, defaultOpen = false }: InfoSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-[15px] bg-white/10 border border-white/15 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-white">{icon}</span>
          <h3 className="font-semibold text-white">{title}</h3>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-white/80">
          {children}
        </div>
      )}
    </div>
  );
}

interface PhoneButtonProps {
  label?: string;
  phone: string;
  onClick: () => void;
  highlight?: boolean;
  compact?: boolean;
}

function PhoneButton({ label, phone, onClick, highlight, compact }: PhoneButtonProps) {
  if (compact) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-1 px-3 py-1.5 bg-pp-orange rounded-[5px] text-white hover:bg-pp-orange/90 transition-colors"
        title={phone}
      >
        <Phone className="w-4 h-4" />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-sm transition-colors ${
        highlight
          ? 'bg-pp-orange text-white hover:bg-pp-orange/90'
          : 'bg-white/20 text-white hover:bg-white/30'
      }`}
    >
      <Phone className="w-3.5 h-3.5" />
      {label && <span>{label}</span>}
    </button>
  );
}

export default PregnancyInfoModal;
